import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Plus, Pencil, Trash2, X, Check, Server, PauseCircle, PlayCircle, ShieldAlert } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Portal = ({ children }) => createPortal(children, document.body);

const EMPTY_MID = {
    vendor: "firstpay", label: "", api_base_url: "", api_key: "",
    webhook_url: "", daily_limit: "", min_payout: "", max_payout: "",
    ip_whitelist: "",
};

export default function VendorsAdminPage() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const [vendors, setVendors] = useState([]);
    const [mids, setMids] = useState([]);
    const [err, setErr] = useState("");
    const [editing, setEditing] = useState(null);  // null | "new" | mid_id
    const [form, setForm] = useState(EMPTY_MID);
    const [busy, setBusy] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);

    const refresh = async () => {
        setErr("");
        try {
            const [v, m] = await Promise.all([
                api.get("/admin/payouts/vendors"),
                api.get("/admin/payouts/mids"),
            ]);
            setVendors(v.data.items || []);
            setMids(m.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { refresh(); }, []);

    const openNew = (vendor_id) => {
        setForm({ ...EMPTY_MID, vendor: vendor_id || "firstpay" });
        setEditing("new");
    };
    const openEdit = (m) => {
        setForm({
            vendor: m.vendor,
            label: m.label,
            api_base_url: m.api_base_url,
            api_key: "",       // never show; blank = don't change
            webhook_url: m.webhook_url || "",
            daily_limit: m.daily_limit ?? "",
            min_payout: m.min_payout ?? "",
            max_payout: m.max_payout ?? "",
            ip_whitelist: (m.ip_whitelist || []).join(", "),
            mid_id: m.mid_id,
        });
        setEditing(m.mid_id);
    };

    const save = async () => {
        setErr(""); setBusy(true);
        try {
            const payload = {
                label: form.label.trim(),
                api_base_url: form.api_base_url.trim(),
                webhook_url: form.webhook_url.trim() || null,
                daily_limit: form.daily_limit === "" ? null : Number(form.daily_limit),
                min_payout: form.min_payout === "" ? null : Number(form.min_payout),
                max_payout: form.max_payout === "" ? null : Number(form.max_payout),
                ip_whitelist: form.ip_whitelist
                    ? form.ip_whitelist.split(",").map((s) => s.trim()).filter(Boolean)
                    : [],
            };
            if (editing === "new") {
                payload.vendor = form.vendor;
                payload.api_key = form.api_key.trim();
                if (!payload.api_key) { setErr("API key is required for new MIDs"); setBusy(false); return; }
                await api.post("/admin/payouts/mids", payload);
            } else {
                if (form.api_key.trim()) payload.api_key = form.api_key.trim();
                await api.patch(`/admin/payouts/mids/${editing}`, payload);
            }
            setEditing(null); setForm(EMPTY_MID);
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const togglePause = async (m) => {
        try {
            await api.patch(`/admin/payouts/mids/${m.mid_id}`, {
                status: m.status === "active" ? "paused" : "active",
            });
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const doDelete = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(`/admin/payouts/mids/${confirmDel.mid_id}`);
            setConfirmDel(null); refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); setConfirmDel(null); }
    };

    const midsByVendor = (vid) => mids.filter((m) => m.vendor === vid);

    if (!isSuper) {
        return (
            <div data-testid="admin-vendors-page" className="p-10 max-w-md mx-auto text-center">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                    <ShieldAlert className="w-10 h-10 mx-auto text-amber-600 mb-3" />
                    <h2 className="font-bold text-base mb-1">Super admin only</h2>
                    <p className="text-xs text-slate-600">
                        Vendor &amp; MID credential management is restricted to super admins.
                        Contact your administrator to request access.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-vendors-page" className="space-y-6 p-6">
            <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Server className="w-6 h-6 text-indigo-600" /> Payout Vendors & MIDs
                </h1>
                <p className="text-xs text-slate-500 mt-1">
                    Each vendor can hold multiple MIDs (credential sets). Assign MIDs to partners from the Payouts admin page.
                </p>
            </div>

            {err && <div data-testid="vendors-error" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            <div className="space-y-4">
                {vendors.map((v) => (
                    <div key={v.id} data-testid={`vendor-card-${v.id}`}
                        className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
                            <div>
                                <div className="font-bold text-base">{v.label}</div>
                                <div className="text-[11px] text-slate-500 font-mono">{v.id}</div>
                            </div>
                            <button onClick={() => openNew(v.id)}
                                    data-testid={`add-mid-${v.id}`}
                                    className="btn-primary px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5">
                                <Plus className="w-3.5 h-3.5" /> Add MID
                            </button>
                        </div>
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50/50 text-slate-500 text-[10px] uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-2 text-left">Label</th>
                                    <th className="px-4 py-2 text-left">API Base URL</th>
                                    <th className="px-4 py-2 text-left">API Key</th>
                                    <th className="px-4 py-2 text-right">Daily / Min / Max</th>
                                    <th className="px-4 py-2 text-center">Status</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {midsByVendor(v.id).length === 0 && (
                                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No MIDs for this vendor. Click "Add MID" to create one.</td></tr>
                                )}
                                {midsByVendor(v.id).map((m) => (
                                    <tr key={m.mid_id} data-testid={`mid-row-${m.mid_id}`}
                                        className="border-t border-slate-100 hover:bg-slate-50/50">
                                        <td className="px-4 py-2.5">
                                            <div className="font-semibold text-sm">{m.label}</div>
                                            <div className="text-[10px] text-slate-400 font-mono">{m.mid_id}</div>
                                        </td>
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-slate-600 max-w-[220px] truncate">{m.api_base_url}</td>
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">{m.api_key_display}</td>
                                        <td className="px-4 py-2.5 text-right text-[11px] text-slate-600">
                                            <div>{m.daily_limit ? `₹${m.daily_limit}` : "—"} / day</div>
                                            <div className="text-slate-400">
                                                {m.min_payout ? `₹${m.min_payout}` : "0"} – {m.max_payout ? `₹${m.max_payout}` : "∞"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            <button onClick={() => togglePause(m)}
                                                    data-testid={`toggle-mid-${m.mid_id}`}
                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                                m.status === "active"
                                                    ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                                    : "bg-slate-200 text-slate-600 hover:bg-slate-300"
                                            }`}>
                                                {m.status === "active" ? <PlayCircle className="w-3 h-3" /> : <PauseCircle className="w-3 h-3" />}
                                                {m.status}
                                            </button>
                                        </td>
                                        <td className="px-4 py-2.5 text-right space-x-2">
                                            <button onClick={() => openEdit(m)} data-testid={`edit-mid-${m.mid_id}`}
                                                    className="text-blue-600 hover:text-blue-800">
                                                <Pencil className="w-3.5 h-3.5 inline" />
                                            </button>
                                            <button onClick={() => setConfirmDel(m)} data-testid={`del-mid-${m.mid_id}`}
                                                    className="text-rose-600 hover:text-rose-800">
                                                <Trash2 className="w-3.5 h-3.5 inline" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>

            {/* Create/Edit modal */}
            {editing && (<Portal>
                <div data-testid="mid-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl p-6 w-[560px] max-h-[90vh] overflow-y-auto shadow-xl">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="font-bold text-lg">
                                    {editing === "new" ? `Add MID — ${form.vendor}` : "Edit MID"}
                                </h3>
                                <p className="text-[11px] text-slate-500 mt-0.5">Credentials never leave the server once saved.</p>
                            </div>
                            <button onClick={() => { setEditing(null); setForm(EMPTY_MID); }} className="text-slate-400 hover:text-slate-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Label</label>
                                <input data-testid="mid-form-label" value={form.label}
                                        onChange={(e) => setForm({ ...form, label: e.target.value })}
                                        placeholder="e.g. FirstPay-SRV-HDFC"
                                        className="input w-full text-sm mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">API Base URL</label>
                                <input data-testid="mid-form-url" value={form.api_base_url}
                                        onChange={(e) => setForm({ ...form, api_base_url: e.target.value })}
                                        placeholder="https://srv.firstpay.online/api/v1"
                                        className="input w-full text-sm mt-1 font-mono" />
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                                    API Key {editing !== "new" && <span className="text-slate-400 normal-case">(leave blank to keep)</span>}
                                </label>
                                <input data-testid="mid-form-key" type="password" value={form.api_key}
                                        onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                                        placeholder={editing === "new" ? "fp_sk_live_..." : "••••••••"}
                                        className="input w-full text-sm mt-1 font-mono" />
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Webhook URL (your endpoint)</label>
                                <input data-testid="mid-form-webhook" value={form.webhook_url}
                                        onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                                        placeholder="https://your.app/api/webhooks/firstpay/payout"
                                        className="input w-full text-sm mt-1 font-mono" />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Daily limit ₹</label>
                                    <input data-testid="mid-form-daily" type="number" value={form.daily_limit}
                                            onChange={(e) => setForm({ ...form, daily_limit: e.target.value })}
                                            placeholder="500000" className="input w-full text-sm mt-1" />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Min ₹</label>
                                    <input data-testid="mid-form-min" type="number" value={form.min_payout}
                                            onChange={(e) => setForm({ ...form, min_payout: e.target.value })}
                                            placeholder="100" className="input w-full text-sm mt-1" />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Max ₹</label>
                                    <input data-testid="mid-form-max" type="number" value={form.max_payout}
                                            onChange={(e) => setForm({ ...form, max_payout: e.target.value })}
                                            placeholder="100000" className="input w-full text-sm mt-1" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">IP whitelist (comma-separated)</label>
                                <input data-testid="mid-form-ips" value={form.ip_whitelist}
                                        onChange={(e) => setForm({ ...form, ip_whitelist: e.target.value })}
                                        placeholder="13.228.86.213, 18.136.43.77"
                                        className="input w-full text-sm mt-1 font-mono" />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => { setEditing(null); setForm(EMPTY_MID); }}
                                    data-testid="mid-form-cancel"
                                    className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button onClick={save} disabled={busy}
                                    data-testid="mid-form-save"
                                    className="btn-primary px-4 py-2 rounded-md text-xs flex items-center gap-1.5">
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                {editing === "new" ? "Create MID" : "Save changes"}
                            </button>
                        </div>
                    </div>
                </div>
            </Portal>)}

            {/* Delete confirm */}
            {confirmDel && (<Portal>
                <div data-testid="mid-delete-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl p-6 w-[400px] shadow-xl">
                        <h3 className="font-bold text-base mb-1">Delete MID?</h3>
                        <p className="text-xs text-slate-600 mb-4">
                            <span className="font-semibold">{confirmDel.label}</span> will be unassigned from every partner using it. This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setConfirmDel(null)} className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button onClick={doDelete} data-testid="mid-delete-confirm"
                                    className="px-3 py-2 rounded-md text-xs bg-rose-600 text-white hover:bg-rose-700 font-semibold">Delete</button>
                        </div>
                    </div>
                </div>
            </Portal>)}
        </div>
    );
}
